import React, { useEffect, useState, useRef } from 'react';
import api from 'api';
import { Br, Cut, Line, Printer, Text, render } from 'react-thermal-printer';
import { API, CHAT } from 'common/config-api';
import CustomKeypad from './CustomKeypad';

/**
 * POS 계산기 컴포넌트
 * 결제 금액 계산 및 구매 완료 처리를 담당합니다.
 */
function PosCalculator({ productList, userInfo }) {
  const apiUrl = `${API.BASE_URL}`;

  // 상태 관리
  const [penny, setPenny] = useState(0);
  const [coupon, setCoupon] = useState(0);
  const [billing, setBilling] = useState('');
  const [showKeypad, setShowKeypad] = useState(false);

  // Refs
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  /**
   * 현재 날짜/시간 반환
   */
  const nowDatetime = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = ('0' + (today.getMonth() + 1)).slice(-2);
    const day = ('0' + today.getDate()).slice(-2);
    const hours = ('0' + today.getHours()).slice(-2);
    const minutes = ('0' + today.getMinutes()).slice(-2);
    const seconds = ('0' + today.getSeconds()).slice(-2);
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // 계산 값들
  const productCount = productList.length;
  let salesProductQuantity = 0;
  let totalPoint = 0;
  let totalPrice = 0;
  const discount = 0;

  productList.forEach((arr) => {
    salesProductQuantity += arr.salesProductQuantity;
    totalPrice += arr.salesProductQuantity * arr.productPrice;
    totalPoint += arr.productPoint;
  });

  /**
   * 받은 금액 입력 처리
   */
  const handleBillingChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (raw === '') {
      setBilling('');
    } else {
      const tempBilling = (parseInt(raw, 10) || 0).toString();
      setBilling(tempBilling);
      const change = (Number(tempBilling) + coupon - totalPrice).toLocaleString('ko-KR');
      setPenny(change);
    }
  };

  /**
   * 쿠폰 금액 입력 처리
   */
  const handleCouponChange = (e) => {
    let tempBilling = e.target.value.replace(/\D/g, '').replace(/^0+(?!$)/, '');
    tempBilling = Number(tempBilling.replace(/[^0-9]/g, ''));
    setCoupon(tempBilling);
    e.target.value = tempBilling.toLocaleString('ko-KR');
    setPenny(((Number(billing) + tempBilling) - totalPrice).toLocaleString('ko-KR'));
  };

  /**
   * 실시간 미리보기 전송 (WebSocket)
   */
  const sendPreview = () => {
    const salesObj = {
      salesPrice: totalPrice,
      salesPoint: 0,
      salesProductCnt: productCount,
      salesReceived: billing,
      salesChange: (Number(billing) - totalPrice),
      userCode: userInfo.userCode,
      userName: userInfo.userNickname,
      salesProducts: productList,
      salesDate: nowDatetime()
    };

    const chatUrl = `${CHAT.CHAT_URL}`;
    const socket = new WebSocket(chatUrl);
    socket.onopen = () => {
      const enterMsg = {
        type: 'TALK',
        roomId: 'girieno',
        sender: 'purchase',
        msg: salesObj
      };
      socket.send(JSON.stringify(enterMsg));
    };
  };

  // 상품 목록 변경 시 미리보기 전송
  useEffect(() => {
    if (productList.length > 0) {
      sendPreview();
    }
  }, [productList]);

  /**
   * 구매 완료 처리
   */
  const completePurchase = async () => {
    // 유효성 검사
    if (!billing) {
      alert('받은 금액을 입력 해 주세요');
      return;
    }

    const pennyValue = typeof penny === 'string'
      ? Number(penny.replaceAll(',', ''))
      : penny;

    if (pennyValue < 0) {
      alert('받은 금액이 부족합니다.');
      return;
    }

    const salesObj = {
      salesPrice: totalPrice,
      salesPoint: 0,
      salesProductCnt: productCount,
      salesReceived: billing,
      salesChange: (Number(billing) - totalPrice),
      userCode: userInfo.userCode,
      userName: userInfo.userNickname,
      salesProducts: productList,
      salesDate: nowDatetime()
    };

    // WebSocket으로 구매 완료 알림
    const chatUrl = `${CHAT.CHAT_URL}`;
    const socket = new WebSocket(chatUrl);
    socket.onopen = () => {
      const enterMsg = {
        type: 'TALK',
        roomId: 'girieno',
        sender: 'purchase',
        msg: salesObj
      };
      socket.send(JSON.stringify(enterMsg));

      const purchaseMsg = {
        type: 'TALK',
        roomId: 'girieno',
        sender: 'purchase',
        msg: 'purchase'
      };
      socket.send(JSON.stringify(purchaseMsg));
    };

    // 영수증 출력
    printPage(salesObj);

    // API 호출
    try {
      await api.post(`${apiUrl}/sales`, salesObj);
      alert('등록되었습니다.');
    } catch (error) {
      console.error('판매 등록 실패:', error);
      alert('판매 등록 중 오류가 발생했습니다.');
    }
  };

  /**
   * 영수증 출력 (브라우저 프린트)
   */
  const printPage = (salesObj) => {
    const printContent = `
      - UENO SHOKAI -<br />
      <br />
      salesDate: ${salesObj.salesDate}<br />
      salesReceived: ${salesObj.salesReceived.toLocaleString('ko-KR')}<br />
      salesPrice: ${salesObj.salesPrice.toLocaleString('ko-KR')}<br />
      <br />
      ${salesObj.salesProducts[0]?.productName || ''}<br />
      ￥${salesObj.salesProducts[0]?.productPrice || 0}<br />
      <br />
      - UENO SHOKAI -<br />
    `;

    const windowObj = window.open(
      '',
      'Print',
      'width=1350, height=800, toolbars=no, scrollbars=no, status=no, resizable=no'
    );
    windowObj.document.writeln(printContent);
    windowObj.document.close();
    windowObj.focus();
    windowObj.print();
    windowObj.close();
  };

  // 키패드 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowKeypad(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * 키패드 숫자 입력
   */
  const handlePress = (digit) => {
    setBilling((prev) => prev + digit);
  };

  /**
   * 키패드 백스페이스
   */
  const handleBackspace = () => {
    setBilling((prev) => prev.slice(0, -1));
  };

  /**
   * 키패드 엔터
   */
  const handleEnter = () => {
    setShowKeypad(false);
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  return (
    <div className="col-lg-4">
      <div className="card">
        <div className="card-body">
          <h5 className="card-title">구매</h5>
          <table className="table">
            <tbody>
              <tr>
                <th scope="row" width="50%">상품 종류</th>
                <td style={{ textAlign: 'right' }}>{productCount}종</td>
              </tr>

              <tr>
                <th scope="row" width="50%">상품 수</th>
                <td style={{ textAlign: 'right' }}>{salesProductQuantity}개</td>
              </tr>

              <tr>
                <th scope="row">소계</th>
                <td style={{ textAlign: 'right' }}>{totalPrice.toLocaleString('ko-KR')}</td>
              </tr>

              <tr>
                <th scope="row">필요포인트</th>
                <td style={{ textAlign: 'right' }}>{totalPoint}</td>
              </tr>

              <tr>
                <th scope="row">할인</th>
                <td style={{ textAlign: 'right' }}>{discount}</td>
              </tr>

              <tr>
                <th scope="row">총금액</th>
                <td style={{ paddingTop: '.5rem', paddingBottom: '.5rem', textAlign: 'right' }}>
                  <span style={{ color: '#0d6efd', fontWeight: 600, fontSize: 'x-large' }}>
                    {totalPrice.toLocaleString('ko-KR')}
                  </span>
                </td>
              </tr>

              <tr>
                <th scope="row">받은금액</th>
                <td style={{ textAlign: 'right' }} ref={containerRef}>
                  <input
                    type="text"
                    name="received"
                    ref={inputRef}
                    value={billing}
                    onChange={handleBillingChange}
                    onFocus={() => setShowKeypad(true)}
                    style={{ textAlign: 'right', borderWidth: 0, width: 120 }}
                  />
                  {showKeypad && (
                    <CustomKeypad
                      onPress={handlePress}
                      onBackspace={handleBackspace}
                      onEnter={handleEnter}
                      style={{ top: '40px', left: '0px' }}
                    />
                  )}
                </td>
              </tr>

              <tr>
                <th scope="row">쿠폰</th>
                <td style={{ textAlign: 'right' }}>
                  <input
                    type="text"
                    name="coupon"
                    defaultValue={coupon}
                    onKeyUp={handleCouponChange}
                    style={{ textAlign: 'right', borderWidth: 0, width: 120 }}
                  />
                </td>
              </tr>

              <tr>
                <th scope="row">거스름돈</th>
                <td style={{ textAlign: 'right' }}>{penny}</td>
              </tr>
            </tbody>
          </table>

          <div className="d-grid gap-2 mt-3">
            <button
              className="btn btn-primary"
              type="button"
              onClick={completePurchase}
            >
              구매완료
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PosCalculator;
